import { Component, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { CoreFacade } from '../../core.facade';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { filter, lastValueFrom, Subscription } from 'rxjs';
import { AuthComponent } from '../../../shared/containers/auth/auth.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { NavbarComponent } from '../../layout/navbar/navbar.component';
import { SidepanelComponent } from '../../layout/sidepanel/sidepanel.component';
import { FooterComponent } from '../../layout/footer/footer.component';
import { Toast } from 'primeng/toast';
import { TimezoneAppItemOutputDto } from '@shared/api/model/timezone-app-item-output-dto';
import { WebSocketFacade } from 'app/notifications/websocket.facade';
import { AuthFacade } from 'app/auth/auth.facade';

@Component({
    selector: 'app-core',
    templateUrl: './core.component.html',
    styleUrls: ['./core.component.scss'],
    imports: [AuthComponent, LoaderComponent, ConfirmDialog, NavbarComponent, SidepanelComponent, RouterOutlet, FooterComponent, Toast]
})
export class CoreComponent implements OnInit, OnDestroy {
  title = 'Aimtec DI';
  menuItems: MenuItem[] = [];
  userItems: MenuItem[] = [];
  actualRoute?: string;

  loading = computed(() => {
    return !this.menuItemsLoaded() || !this.userItemsLoaded() || !this.timezoneLoaded();
  });
  
  menuItemsLoaded = signal(false);
  userItemsLoaded = signal(false);
  timezoneLoaded = signal(false);

  readonly notificationsAuthorized = signal(false);

  timezone?: TimezoneAppItemOutputDto;

  private subscriptions: Subscription[] = [];

  constructor(private authFacade: AuthFacade, private coreFacade: CoreFacade, private router: Router, private webSocketFacade: WebSocketFacade) {
    this.title = $localize`Aimtec DI`;

    this.webSocketFacade.openConnection();

    // authorize web socket connection
    effect(async () => {
      if (this.loading() || this.notificationsAuthorized()) {
        return;
      }

      const token = await lastValueFrom(this.authFacade.getAccessToken());
      this.webSocketFacade.authorizeConnection(token);

      this.notificationsAuthorized.set(true);
    });
  }

  ngOnInit(): void {
    this.coreFacade.init();
    this.actualRoute = window.location.pathname;

    this.coreFacade.loadMenuItems();

    this.subscriptions.push(this.coreFacade.menuItems$.subscribe(menuItems => {
      this.menuItems = menuItems;
      this.menuItemsLoaded.set(true);
    }));

    this.subscriptions.push(this.coreFacade.userItems$.subscribe(userItems => {
      this.userItems = userItems;
      this.userItemsLoaded.set(true);
    }));    

    this.subscriptions.push(this.coreFacade.timezone$.subscribe(timezone => {
      if (!timezone) {
        return;
      }

      this.timezone = timezone;
      this.timezoneLoaded.set(true);
    }));  

    this.setupRouterEvents();
  }  

  setupRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart)
      )
      .subscribe(() => {
        this.clearMenuPopups();
      });
  }

  /**
   * Fix for PrimeNG bug where popups are not removed if they are open while navigating to another page.
   * They stay open on the other page with no way of closing them.
   */
  private clearMenuPopups(): void {
    const menuElements = document.querySelectorAll('body > [name="p-anchored-overlay"]');
    menuElements.forEach((node) => {
      node.remove();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // hides app content after timezone is set
  // so the app has time to refresh all displayed dates on current page
  onTimezoneSet() {
    this.timezoneLoaded.set(false);

    setTimeout(() => {
      this.timezoneLoaded.set(true);
    }, 500);
  }
}
